import { memo } from "react";
import styles from "./ConfirmationModal.module.scss";
import Button from "../button/Button";

interface ConfirmationModalProps {
	isOpen: boolean;
	onClose?: () => void;
	onConfirm?: () => void;
	title?: string;
	message?: string;
	confirmText?: string;
	cancelText?: string;
	isLoading?: boolean;
	variant?: "danger" | "warning" | "info";
	icon?: React.ReactElement;
	description?: string;
	subtitle?: string;
}

const ConfirmationModal = memo(({
	isOpen,
	onClose,
	onConfirm,
	title,
	message,
	confirmText = "Confirm",
	cancelText = "Cancel",
	isLoading = false,
	variant = "danger",
	icon,
	description,
	subtitle,
}: ConfirmationModalProps) => {
	if (!isOpen) return null;

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose?.();
		}
	};

	return (
		<div className={styles.backdrop} onClick={handleBackdropClick}>
			<div className={styles.modal}>
				{(title || subtitle || onClose) && (
					<div className={styles.header}>
						<div className={styles.headerText}>
							{title && <h2 className={styles.title}>{title}</h2>}
							{description && <p className={styles.description}>{description}</p>}
						</div>
						{onClose && (
							<button className={styles.closeButton} onClick={onClose} disabled={isLoading}>
								×
							</button>
						)}
					</div>
				)}
				<div className={styles.body}>
					{icon && <div className={styles.icon}>{icon}</div>}
					{subtitle && <h3 className={styles.subtitle}>{subtitle}</h3>}
					{message && <p className={styles.message}>{message}</p>}
				</div>
				{(onClose || onConfirm) && (
					<div className={styles.footer}>
						{onClose && (
							<Button text={cancelText} variant="outline" onClick={onClose} disabled={isLoading} />
						)}
						{onConfirm && (
							<Button
								text={confirmText}
								variant={variant === "danger" ? "danger" : "primary"}
								onClick={onConfirm}
								disabled={isLoading}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);
});

export default ConfirmationModal;